import * as React from 'react';
import { useReactTable, getCoreRowModel, ColumnOrderState } from '@tanstack/react-table';
import isNil from 'lodash/isNil';
import { I18n } from 'react-redux-i18n';

import { ErrorMessage } from '@common/components/error-message';
import { HttpErrorDto } from '@common/dto/http';
import { NewTableColumnAction, NewTableColumnId, NewTableDefaultDataItem, NewTableField, NewTableFilterValue, NewTableFolder, NewTableHeaderAction, NewTableLoadMetadata, NewTableLoadMetadataSorting, NewTableProps, NewTableRowAction } from '../new-table.types';
import { processColumnActions } from './columns';
import { processHeaderActions } from './header';
import { MessageProps } from '@common/components/MessageBox.types';
import { TreeGetIdArgs } from '@common/components/Tree/tree.types';
import { scrollTopLeft as scrollTopLeftBase } from '../new-table.utils';

enum ActionSource {
  headerCell = 'headerCell',
  header = 'header',
  row = 'row',
}

export const useController = (props: NewTableProps) => {
  const { 
    data,
    columns,
    disableAutoInit,
    loadMetadata,
    loadItemsCount = 45,
    onLoadData,
    columnActions = [],
    columnsManagement,
    headerActions = [],
    selection,
    itemUniqProperty,
    rowActions = [],
    columnsResizing,
    totalItemsCount = 0,
    moduleName,
    infiniteScroll,
    columnsOrder,
    loading: loadingFromOutside,
    tableRef,
    errorMessage,
  } = props;

  if (!itemUniqProperty) {
    console.error(`
    
    
    ${moduleName}: NewTable: itemUniqProperty is missed.

    
    `);
  }

  const rootRef = tableRef || React.useRef<HTMLDivElement>();

  const [currentActions, setCurrentActions] = React.useState<{
    source: ActionSource; 
    coords: { x: number; y: number } 
  } | undefined>();
  const [currentContextMenuColumnId, setCurrentContextMenuColumnId] = React.useState<NewTableColumnId | undefined>();
  const [isOpenedFieldSelector, setOpenedFieldSelectorState] = React.useState(false);
  const [areOpenedFilters, setOpenedFiltersState] = React.useState(false);
  const [isLoading, setLoading] = React.useState(false);
  const [messageProps, setMessageProps] = React.useState<MessageProps>();

  const columnsId = React.useMemo(() => columns.map((x) => x.id as string), [columns]);

  const computedLoading = {
    list: isLoading || Boolean(loadingFromOutside?.list),
    columns: loadingFromOutside?.columns
  };

  const tableInstance = useReactTable({
    state: {
      columnOrder: columnsId,
    },
    columnResizeMode: 'onEnd',
    data,
    columns,
    getCoreRowModel: getCoreRowModel() as any,
  });

  const scrollTopLeft = React.useCallback(() => {
    if (rootRef.current) {
      scrollTopLeftBase(rootRef.current);
    }
  }, []);

  const closeMessage = React.useCallback(() => {
    setMessageProps(undefined);
  }, []);

  const showMessage = React.useCallback((props: Omit<MessageProps, 'show'>) => {
    setMessageProps({
      ...props,
      show: true,
      onCustomOk: closeMessage,
    });
  }, [closeMessage]);

  const showErrorMessage = React.useCallback((message: React.ReactNode) => {
    showMessage({
      header: I18n.t('Operation canceled'),
      content: <ErrorMessage children={message} />,
      size: 'small',
      confirmText: I18n.t('Ok')
    })
  }, [showMessage]);

  const getData = React.useCallback(async (params: NewTableLoadMetadata) => {
    if (!onLoadData) {
      return;
    }

    try {
      setLoading(true);
      await onLoadData(params);
    } catch (err) {
      console.error(err);
      showErrorMessage((err as HttpErrorDto)?.entity || errorMessage.list);
    } finally {
      setLoading(false);
    }
  }, [onLoadData]);

  React.useEffect(() => {
    if (!disableAutoInit && onLoadData) {
      const initialMetadata: NewTableLoadMetadata = {
        filtering: loadMetadata?.filtering,
        sorting: loadMetadata?.sorting,
        pagination: loadMetadata?.pagination || {
          start: 0,
          size: loadItemsCount
        }
      };

      getData(initialMetadata);
    }
  }, []);

  const handleSort = React.useCallback((sorting: NewTableLoadMetadataSorting) => {
    scrollTopLeft();
    getData({
      filtering: loadMetadata?.filtering,
      pagination: {
        start: 0,
        size: loadMetadata?.pagination.size || loadItemsCount,
      },
      sorting,
    });
  }, [getData, loadMetadata, loadItemsCount, scrollTopLeft]);

  const shouldIgnoreLazyLoading = React.useMemo(() => {
    const hasAnyLoading = Object.values(computedLoading).some((x) => x);
		const shouldIgnore = hasAnyLoading || data.length === totalItemsCount || data.length > totalItemsCount;

		return shouldIgnore;
	}, [totalItemsCount, data.length, computedLoading]);

  const handleLazyLoad = () => {
    if (shouldIgnoreLazyLoading) {
      return;
    }

    getData({
      filtering: loadMetadata?.filtering,
      pagination: {
        start: data.length,
        size: loadMetadata?.pagination.size || loadItemsCount,
      },
      sorting: loadMetadata?.sorting,
    });
  };

  const handleColumnRemove = React.useCallback(async () => {
    if (columnsManagement?.onRemove && !isNil(currentContextMenuColumnId)) {
      try {
        await columnsManagement.onRemove(currentContextMenuColumnId);
      } catch (err) {
        console.error(err);
        showErrorMessage((err as HttpErrorDto | undefined)?.entity || I18n.t('Failed to save columns'));
      }
    }
  }, [currentContextMenuColumnId, columnsManagement?.onRemove]);

  const handleColumnAdd = React.useCallback(async (data: {
    field: NewTableField,
    folder: NewTableFolder;
  }) => {
    if (columnsManagement?.onAdd) { 
      try {
        const lastColumnId = columns[columns.length - 1]?.id;

        await columnsManagement.onAdd({
          field: data.field,
          folder: data.folder,
          columnId: currentContextMenuColumnId ?? lastColumnId!,
        });
      } catch (err) {
        console.error(err);
        showErrorMessage((err as HttpErrorDto | undefined)?.entity || I18n.t('Failed to save columns'));
      }
    }
  }, [columnsManagement?.onAdd, columns, currentContextMenuColumnId]);

  const openFieldSelector = React.useCallback(() => setOpenedFieldSelectorState(true), []);
  const closeFieldSelector = React.useCallback(() => setOpenedFieldSelectorState(false), []);

  const openFilters = React.useCallback(() => setOpenedFiltersState(true), []);
  const closeFilters = React.useCallback(() => setOpenedFiltersState(false), []);

  const applyColumnFilters = (filterValue?: NewTableFilterValue) => {
    closeFilters();

    if (isNil(currentContextMenuColumnId)) {
      return;
    }

    const currentFilters = { ...loadMetadata?.filtering?.columns }

    if (filterValue) {
      currentFilters[currentContextMenuColumnId] = filterValue;
    } else {
      delete currentFilters[currentContextMenuColumnId];
    }

    scrollTopLeft();
    getData({
      pagination: {
        start: 0,
        size: loadMetadata?.pagination.size || loadItemsCount,
      },
      sorting: loadMetadata?.sorting,
      filtering: {
        filter: true,
        columns: currentFilters
      },
    });
  };

  const clearColumnFilter = () => {
    closeFilters();

    if (isNil(currentContextMenuColumnId)) {
      return;
    }

    const currentFilters = { ...loadMetadata?.filtering?.columns }

    delete currentFilters[currentContextMenuColumnId];

    const hasFilters = Object.keys(currentFilters).length > 0;

    scrollTopLeft();
    getData({
      pagination: {
        start: 0,
        size: loadMetadata?.pagination.size || loadItemsCount,
      },
      sorting: loadMetadata?.sorting,
      filtering: {
        filter: hasFilters,
        columns: currentFilters
      },
    });
  };

  const clearColumnSorting = () => {
    scrollTopLeft();
    getData({
      pagination: {
        start: 0,
        size: loadMetadata?.pagination.size || loadItemsCount,
      },
      sorting: undefined,
      filtering: loadMetadata?.filtering,
    });
  };

  const processedColumnActions = processColumnActions({
    loadMetadata,
    columnActions,
    currentContextMenuColumnId,
    filtering: loadMetadata?.filtering,
    openFieldSelector,
    removeColumn: handleColumnRemove,
    openFilters,
    clearFilter: clearColumnFilter,
    clearSorting: clearColumnSorting,
  });

  const handleHeaderCellContextMenu = React.useCallback((params: { 
    columnId: NewTableColumnId;
    coords: { x: number; y: number } 
  }) => {
    if (processedColumnActions?.length) {
      setCurrentContextMenuColumnId(params.columnId);
      setCurrentActions({
        source: ActionSource.headerCell,
        coords: params.coords,
      });
    }
  }, [processedColumnActions?.length]);

  const processedHeaderActions = processHeaderActions({
    headerActions,
    openFieldSelector,
  });

  const handleHeaderContextMenu = React.useCallback((params: {
    coords: { x: number; y: number } 
  }) => {
    if (processedHeaderActions?.length) {
      setCurrentContextMenuColumnId(undefined);
      setCurrentActions({
        source: ActionSource.header,
        coords: params.coords,
      });
    }
  }, [processedHeaderActions]);

  const closeHeaderActions = React.useCallback(() => {
    setCurrentActions(undefined);
  }, []);

  const getMenuHorizontalPosition = React.useCallback((xClickPosition: number) => {
		const { innerWidth } = window;
		const half = innerWidth / 2;

		if (xClickPosition > half) {
			return {
				right: innerWidth - xClickPosition,
			};
		}

		return {
			left: xClickPosition,
		};
	}, []);

  const getMenuVerticalPosition = React.useCallback((yClickPosition: number) => {
		const { innerHeight } = window;
		const half = innerHeight / 2;

		if (yClickPosition > half) {
			return {
				bottom: innerHeight - yClickPosition,
			};
		}

		return {
			top: yClickPosition,
		};
	}, []);

  const handleRowClick = React.useCallback(({ item, e }: { item: NewTableDefaultDataItem; e: React.MouseEvent<HTMLTableRowElement> }) => {
    const alreadySelectedItems = selection?.items || [];
    let newSelectedItems = [item];
    const isAlreadySelected = alreadySelectedItems.some((x) => x[itemUniqProperty] === item[itemUniqProperty]);

    if ((e.ctrlKey || e.shiftKey) && selection?.type === 'multiple') {
      newSelectedItems = isAlreadySelected
        ? alreadySelectedItems.filter((x) => x[itemUniqProperty] !== item[itemUniqProperty])
        : alreadySelectedItems.concat(item);
    } else if (alreadySelectedItems.length === 1 && isAlreadySelected) {
      return;
    }

    if (selection?.onChange) {
      selection.onChange(newSelectedItems);
    }
  }, [selection, itemUniqProperty]);

  const isRowSelected = React.useCallback((item: NewTableDefaultDataItem) => {
    return selection?.items.some((x) => x[itemUniqProperty] === item[itemUniqProperty]) ?? false;
  }, [selection?.items, itemUniqProperty]);

  const handleRowContextMenu = React.useCallback((e: React.MouseEvent<HTMLTableRowElement>) => {
    e.stopPropagation();
    e.preventDefault();

    if ((selection?.items.length || 0) > 0) {
      setCurrentActions({
        source: ActionSource.row,
        coords: {
          x: e.clientX,
          y: e.clientY,
        },
      });
    }
  }, [selection?.items.length]);

  const handleColumnSizeChange = async () => {
    if (columnsResizing?.onChange) {
      const { columnSizingInfo } = tableInstance.getState();
      const resizedColumnId = columnSizingInfo.isResizingColumn;
      const resizedWidth = (columnSizingInfo.startSize || 0) + (columnSizingInfo.deltaOffset || 0);

      try {
        const columnsSize = tableInstance.getAllColumns().reduce<Record<NewTableColumnId, number>>((acc, col) => {
          acc[col.id] = col.id === resizedColumnId ? resizedWidth : col.getSize();

          return acc;
        }, {});

        await columnsResizing?.onChange(columnsSize);
      } catch (err) {
        console.error(err);
        showErrorMessage((err as HttpErrorDto | undefined)?.entity || I18n.t('Failed to save columns'));
      }
    }
  };

  const actionsConfig: Record<ActionSource, Array<NewTableColumnAction | NewTableHeaderAction | NewTableRowAction>> = {
    [ActionSource.header]: processedHeaderActions,
    [ActionSource.headerCell]: processedColumnActions,
    [ActionSource.row]: rowActions,
  };

  const shouldHaveRowClickHandler = Boolean(selection);
  const currentColumn = columns.find((x) => x.id === currentContextMenuColumnId);

  const handleColumnsOrderChange = React.useCallback(async (columns: ColumnOrderState) => {
    if (columnsOrder?.onChange) {
      try {
        await columnsOrder.onChange(columns);
      } catch (err) {
        console.error(err);
        showErrorMessage((err as HttpErrorDto | undefined)?.entity || I18n.t('Failed to save columns'));
      }
    }
  }, [columnsOrder?.onChange, showErrorMessage]);

  const withDragAndDrop = columns.some((x) => x.enableDragAndDrop);

  const getFieldSelectorFieldId = React.useCallback((field: unknown, rowIndex: number) => `bsgrid-${rowIndex}-0`, []);
  const getFieldSelectorFolderId = React.useCallback(({ parentIndex, ownIndex, data }: TreeGetIdArgs) => parentIndex === undefined
    ? `awhs-folder-${ownIndex}`
    : `awhs-folder-child-${data.id}`, []);
  const getFieldSelectorFolderCollapseIconId = React.useCallback(({ parentIndex, ownIndex, data }: TreeGetIdArgs) => parentIndex === undefined
    ? `awhs-folder-${ownIndex}-exp-icon`
    : `awhs-folder-child-${data.id}-exp-icon`, []);
  const getFieldSelectorFolderIconId = React.useCallback(({ parentIndex, ownIndex, data }: TreeGetIdArgs) => parentIndex === undefined
    ? `awhs-folder-${ownIndex}-fld-icon`
    : `awhs-folder-child-${data.id}-fld-icon`, []);

  return {
    tableInstance,
    currentActions,
    currentContextMenuColumnId,
    isOpenedFieldSelector,
    areOpenedFilters,
    actions: currentActions?.source ? actionsConfig[currentActions.source] : undefined,
    loading: computedLoading,
    currentColumn,
    messageProps,
    rootRef,
    getFieldSelectorFolderIconId,
    getFieldSelectorFolderCollapseIconId,
    getFieldSelectorFolderId,
    getFieldSelectorFieldId,
    closeMessage,
    showMessage,
    applyColumnFilters,
    handleLazyLoad: infiniteScroll ? handleLazyLoad : undefined,
    handleColumnSizeChange: columnsResizing ? handleColumnSizeChange : undefined,
    handleRowContextMenu: rowActions?.length ? handleRowContextMenu : undefined,
    isRowSelected,
    handleRowClick: shouldHaveRowClickHandler ? handleRowClick : undefined,
    closeFilters,
    handleSort,
    handleHeaderCellContextMenu: processedColumnActions?.length ? handleHeaderCellContextMenu : undefined,
    getMenuHorizontalPosition,
    getMenuVerticalPosition,
    closeHeaderActions,
    closeFieldSelector,
    handleColumnAdd,
    handleHeaderContextMenu: processedHeaderActions.length ? handleHeaderContextMenu : undefined,
    handleColumnsOrderChange: withDragAndDrop ? handleColumnsOrderChange : undefined,
  };
};